import { numFont } from '../../../utils/fonts';
import React, { useState } from 'react';
import { Box, Popover, Button, Typography, Stack } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  useTimeclockStore,
  selectDaySession,
  selectWoSession,
  selectBreakSession,
  useDayPresenceSeconds,
} from '../../../storeTimeclock';
import { useClockController } from './useClockController';
import WorkOrderPicker from './WorkOrderPicker';
import { TOKENS, Plate, LiveDot, hms, useNow, elapsedSeconds } from './worktimeLib';

/**
 * Persistent header/sidebar clock pill + popover. Styled light-on-navy (it lives in the navy
 * sidebar / mobile AppBar). State comes from the app-wide /active poll; elapsed ticks locally.
 */
export default function TopBarClockWidget({ compact = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config = useTimeclockStore((s) => s.config);
  const daySession = useTimeclockStore(selectDaySession);
  const woSession = useTimeclockStore(selectWoSession);
  const breakSession = useTimeclockStore(selectBreakSession);
  const ctrl = useClockController();
  const nowMs = useNow();
  const [anchor, setAnchor] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const track = config?.track || 'both';
  const hasDay = track !== 'work_order';
  const onWo = !!woSession;
  const onBreak = !!breakSession;
  const daySec = useDayPresenceSeconds(nowMs); // same source of truth as the tablet/page
  const woSec = woSession ? elapsedSeconds(woSession.started_at, nowMs) : 0;
  const breakSec = breakSession ? elapsedSeconds(breakSession.started_at, nowMs) : 0;

  const open = Boolean(anchor);

  const endDay = async () => {
    if (!daySession) return;
    setAnchor(null);
    await ctrl.clockOutSession(daySession.id);
    if ((config?.handling || 'confirm') !== 'auto') {
      navigate('/time-clock/confirm');
    }
  };

  const onPick = (m) => {
    setPickerOpen(false);
    if (woSession && woSession.maintenance !== m.id) {
      ctrl.requestSwitch(m.id, {
        fromReg: woSession.maintenance_info?.vehicle_registration,
        toReg: m.vehicle?.registration_number,
        fromMaintenanceId: woSession.maintenance,
      });
    } else {
      ctrl.startWo(m.id);
    }
  };

  // Nothing running → an explicit "not tracking" pill so it's obvious the clock is off (and a
  // clear entry point into the tablet clock). Dashed/muted to read as inactive, not alarming.
  if (!daySession && !woSession && !breakSession) {
    return (
      <>
        <Box
          onClick={() => navigate('/time-clock')}
          aria-label={t('worktime.idle.notTracking')}
          sx={{
            // Full-width in the desktop sidebar like the running pill below; content-sized in
            // the mobile AppBar
            display: compact ? 'inline-flex' : 'flex',
            width: compact ? 'auto' : '100%',
            alignItems: 'center',
            gap: 0.75,
            maxWidth: '100%',
            boxSizing: 'border-box',
            px: 1.25,
            py: 0.5,
            borderRadius: 20,
            cursor: 'pointer',
            backgroundColor: 'rgba(255,255,255,0.06)',
            border: '1px dashed rgba(255,255,255,0.35)',
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          <Box sx={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t('worktime.idle.notTracking')}
          </Box>
        </Box>
        {ctrl.dialogs}
      </>
    );
  }

  const pillBg = onWo ? TOKENS.green : onBreak ? TOKENS.amber : 'rgba(255,255,255,0.12)';
  const pillBorder = onWo ? TOKENS.green : onBreak ? TOKENS.amber : 'rgba(255,255,255,0.25)';

  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          // Full-width in the desktop sidebar; content-sized in the mobile AppBar so it doesn't
          // stretch across the bar.
          display: compact ? 'inline-flex' : 'flex',
          flexDirection: onWo || onBreak ? 'column' : 'row',
          alignItems: onWo || onBreak ? 'stretch' : 'center',
          gap: onWo || onBreak ? 0.25 : 1,
          width: compact ? 'auto' : '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          px: 1.25,
          py: 0.5,
          borderRadius: onWo || onBreak ? 2 : 20,
          cursor: 'pointer',
          backgroundColor: pillBg,
          border: `1px solid ${pillBorder}`,
          color: '#fff',
        }}
      >
        {onWo || onBreak ? (
          <>
            {/* Row 1: active WO/break timer + reg. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {onBreak ? <RestaurantIcon sx={{ fontSize: 13, flexShrink: 0 }} /> : <LiveDot color="#fff" size={7} />}
              <Box sx={{ ...numFont, fontWeight: 700, fontSize: 14 }}>{hms(onWo ? woSec : breakSec)}</Box>
              <Box sx={{ fontSize: 12, opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {onWo ? woSession.maintenance_info?.vehicle_registration : t('worktime.onBreak.short')}
              </Box>
            </Box>
            {/* Row 2: cumulative day total, so it never looks like it reset. */}
            {hasDay && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.8 }}>
                <AccessTimeIcon sx={{ fontSize: 12 }} />
                <Box sx={{ ...numFont, fontSize: 11 }}>{hms(daySec)}</Box>
                <Box sx={{ fontSize: 11 }}>{t('worktime.day.short')}</Box>
              </Box>
            )}
          </>
        ) : (
          <>
            <LiveDot color="#fff" size={7} />
            <Box sx={{ ...numFont, fontWeight: 700, fontSize: 14 }}>{hms(daySec)}</Box>
            <Box sx={{ fontSize: 12, opacity: 0.9 }}>{t('worktime.day.short')}</Box>
          </>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 320, p: 0, borderRadius: 3, overflow: 'hidden' } } }}
      >
        {onWo ? (
          <Box sx={{ background: `linear-gradient(135deg, ${TOKENS.green} 0%, ${TOKENS.greenDark} 100%)`, p: 2, color: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <LiveDot color="#fff" size={7} /> {t('worktime.onWo.running')}
              </Box>
              <Plate size={13} color="#fff">{woSession.maintenance_info?.vehicle_registration || '—'}</Plate>
            </Box>
            <Typography sx={{ ...numFont, fontSize: 32, fontWeight: 700 }}>{hms(woSec)}</Typography>
            {hasDay && (
              <Typography sx={{ ...numFont, fontSize: 12, opacity: 0.85, mt: 0.5 }}>
                {t('worktime.dayTotal')}: {hms(daySec)}
              </Typography>
            )}
          </Box>
        ) : onBreak ? (
          <Box sx={{ backgroundColor: TOKENS.amber, p: 2, color: '#fff' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <RestaurantIcon sx={{ fontSize: 14 }} /> {t('worktime.onBreak.running')}
            </Box>
            <Typography sx={{ ...numFont, fontSize: 32, fontWeight: 700 }}>{hms(breakSec)}</Typography>
            {hasDay && (
              <Typography sx={{ ...numFont, fontSize: 12, opacity: 0.85, mt: 0.5 }}>
                {t('worktime.dayTotal')}: {hms(daySec)}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ backgroundColor: TOKENS.navy, p: 2, color: '#fff' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <LiveDot color="#fff" size={7} /> {t('worktime.day.running')}
            </Box>
            <Typography sx={{ ...numFont, fontSize: 32, fontWeight: 700 }}>{hms(daySec)}</Typography>
          </Box>
        )}

        <Stack spacing={1} sx={{ p: 2 }}>
          {onWo ? (
            <>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth size="small" startIcon={<RestaurantIcon />} onClick={ctrl.startBreak} disabled={ctrl.busy} sx={{ backgroundColor: TOKENS.amber, color: '#fff', '&:hover': { backgroundColor: TOKENS.amberText } }}>
                  {t('worktime.onWo.break')}
                </Button>
                <Button fullWidth size="small" variant="outlined" startIcon={<SwapHorizIcon />} onClick={() => setPickerOpen(true)} disabled={ctrl.busy}>
                  {t('worktime.onWo.switch')}
                </Button>
              </Box>
              <Button fullWidth size="small" variant="outlined" startIcon={<CheckCircleIcon />} onClick={() => ctrl.clockOutSession(woSession.id)} disabled={ctrl.busy} sx={{ color: TOKENS.red, borderColor: TOKENS.redBorder }}>
                {t('worktime.onWo.endThis')}
              </Button>
              {woSession.maintenance && (
                <Button fullWidth size="small" variant="text" startIcon={<OpenInNewIcon />} onClick={() => { setAnchor(null); navigate(`/maintenances/${woSession.maintenance}`); }}>
                  {t('worktime.onWo.openOrder')}
                </Button>
              )}
            </>
          ) : onBreak ? (
            <Button fullWidth size="small" startIcon={<CheckCircleIcon />} onClick={() => ctrl.clockOutSession(breakSession.id)} disabled={ctrl.busy} sx={{ backgroundColor: TOKENS.green, color: '#fff', '&:hover': { backgroundColor: TOKENS.greenDark } }}>
              {t('worktime.onBreak.end')}
            </Button>
          ) : (
            <>
              <Button fullWidth size="small" startIcon={<PlayArrowIcon />} onClick={() => setPickerOpen(true)} disabled={ctrl.busy} sx={{ backgroundColor: TOKENS.green, color: '#fff', '&:hover': { backgroundColor: TOKENS.greenDark } }}>
                {t('worktime.day.startWo')}
              </Button>
              <Button fullWidth size="small" variant="outlined" startIcon={<RestaurantIcon />} onClick={ctrl.startBreak} disabled={ctrl.busy} sx={{ color: TOKENS.amberText, borderColor: TOKENS.amber }}>
                {t('worktime.onBreak.start')}
              </Button>
              {hasDay && (
                <Button fullWidth size="small" variant="outlined" startIcon={<StopCircleIcon />} onClick={endDay} disabled={ctrl.busy} sx={{ color: TOKENS.red, borderColor: TOKENS.redBorder }}>
                  {t('worktime.day.endDay')}
                </Button>
              )}
            </>
          )}
          <Button fullWidth size="small" variant="text" onClick={() => { setAnchor(null); navigate('/time-clock'); }}>
            {t('worktime.openFull')}
          </Button>
        </Stack>
      </Popover>

      {ctrl.dialogs}
      <WorkOrderPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={onPick} excludeMaintenanceId={woSession?.maintenance} />
    </>
  );
}
